import { createContext, useContext, useState, useCallback } from 'react';

export interface TaraSuggestion {
  label: string;
  prompt: string;
}

interface TaraCtx {
  open: boolean;
  setOpen: (v: boolean) => void;
  suggestion: TaraSuggestion | null;
  setSuggestion: (s: TaraSuggestion | null) => void;
  // A prompt queued to auto-send once the panel opens (e.g. from a suggestion pill).
  pendingPrompt: string | null;
  clearPendingPrompt: () => void;
  // Open the panel and queue a prompt to send.
  askTara: (prompt: string) => void;
}

const TaraContext = createContext<TaraCtx>({
  open: false,
  setOpen: () => {},
  suggestion: null,
  setSuggestion: () => {},
  pendingPrompt: null,
  clearPendingPrompt: () => {},
  askTara: () => {},
});

export function TaraProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [suggestion, setSuggestion] = useState<TaraSuggestion | null>(null);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);

  const askTara = useCallback((prompt: string) => {
    setPendingPrompt(prompt);
    setSuggestion(null);
    setOpen(true);
  }, []);

  const clearPendingPrompt = useCallback(() => setPendingPrompt(null), []);

  return (
    <TaraContext.Provider
      value={{ open, setOpen, suggestion, setSuggestion, pendingPrompt, clearPendingPrompt, askTara }}
    >
      {children}
    </TaraContext.Provider>
  );
}

export function useTaraUI(): TaraCtx {
  return useContext(TaraContext);
}
